/**
 * GenieOwnerDetail — shared immersive chrome for the 4 owner-view detail tabs.
 *
 * The "owner-as-viewer" model: a calm, read-only immersive page. A slim top bar
 * carries ALL the chrome — back to the gallery, a tiny genie identity (the
 * genie's REAL brand logo via resolveGenieAvatarUrl, mascot as fallback) with a
 * Live dot, the four-tab pill nav (Overview / Conversations / Analysis /
 * Knowledge) driven by `?tab=`, a quiet "Manage" door into the configurator,
 * and the single "Tell our genie" change door. Each tab supplies its body via
 * `children`.
 *
 * Loads the genie via the REAL `useAgent(id)` hook. Brand law: genie tokens only;
 * Live dot uses `genie-success`; signature gradient for the change door.
 */
import type { ReactNode } from "react";
import { ChevronLeft, type LucideIcon } from "lucide-react";
import {
  BarChart3,
  BookOpen,
  Home as HomeIcon,
  MessageSquare,
  SlidersHorizontal,
  Sparkles,
} from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAgent } from "@/hooks/agent/useAgents";
import { resolveGenieAvatarUrl } from "@/utils/image/genieAvatars";
import { VoiceMascot } from "@/components/voice-genie/VoiceMascot";
import type { Agent } from "@/types/agent/agent";
import type { VoiceMascotKey } from "@/types/voice-vision";

/** Brand consts (wireframe purple -> genie-secondary; green -> genie-success). */
export const OWNER_BRAND = {
  blue: "#4E9CFF",
  pink: "#FF4DB8",
  /** wireframe purple mapped onto genie-secondary accents */
  purple: "#FF4DB8",
  amber: "#FFBB33",
  /** wireframe Live/Active green mapped onto genie-success (brand blue) */
  green: "#4E9CFF",
} as const;

/** Named owner icons backed by Lucide (inline UI only — never page chrome). */
const OWNER_ICONS: Record<string, LucideIcon> = {
  overview: HomeIcon,
  conversations: MessageSquare,
  analysis: BarChart3,
  knowledge: BookOpen,
  sparkle: Sparkles,
  back: ChevronLeft,
};

export const OwnerIcon = ({
  name,
  size = 16,
  className,
}: {
  name: keyof typeof OWNER_ICONS | string;
  size?: number;
  className?: string;
}) => {
  const Icon = OWNER_ICONS[name] ?? HomeIcon;
  return <Icon style={{ width: size, height: size }} className={className} />;
};

export type OwnerTab = "overview" | "conversations" | "analysis" | "knowledge";

const TABS: { id: OwnerTab; label: string; icon: LucideIcon }[] = [
  { id: "overview", label: "Overview", icon: HomeIcon },
  { id: "conversations", label: "Conversations", icon: MessageSquare },
  { id: "analysis", label: "Analysis", icon: BarChart3 },
  { id: "knowledge", label: "Knowledge", icon: BookOpen },
];

const VALID_TABS: OwnerTab[] = TABS.map((t) => t.id);

/** Resolve a mascot key from a real genie (best-effort, falls back to receptionist). */
export function mascotForAgent(agent?: Agent | null): VoiceMascotKey {
  const hint = `${agent?.type ?? ""} ${agent?.category ?? ""}`.toLowerCase();
  if (hint.includes("support")) return "support";
  if (hint.includes("sales")) return "sales";
  if (hint.includes("book")) return "booking";
  if (hint.includes("survey")) return "survey";
  if (hint.includes("feedback")) return "feedback";
  if (hint.includes("concierge")) return "concierge";
  if (hint.includes("after")) return "after-hours";
  if (hint.includes("onboard")) return "onboarding-role";
  return "receptionist";
}

interface GenieOwnerDetailProps {
  /** Optional override; otherwise reads `?tab` (default "overview"). */
  active?: OwnerTab;
  children: ReactNode;
}

/** Full immersive owner-view shell. */
export const GenieOwnerDetail = ({ active, children }: GenieOwnerDetailProps) => {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: agent } = useAgent(id);
  const brandLogoUrl = agent ? resolveGenieAvatarUrl(agent) : undefined;

  const tabParam = (searchParams.get("tab") as OwnerTab | null) ?? "overview";
  const activeTab: OwnerTab = active ?? (VALID_TABS.includes(tabParam) ? tabParam : "overview");

  const setTab = (tab: OwnerTab) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next);
  };

  const name = agent?.name ?? "Your genie";
  const isLive = agent?.is_active ?? true;
  const mascot = mascotForAgent(agent);

  return (
    <div className="flex flex-col h-screen w-full bg-genie-background overflow-hidden">
      {/* slim immersive top bar */}
      <div className="h-14 flex-shrink-0 px-4 bg-white border-b border-[#F3F4F6] flex items-center justify-between gap-3.5">
        <div className="flex items-center gap-3 min-w-0">
          {/* back to gallery */}
          <button
            type="button"
            onClick={() => navigate("/genies")}
            className="inline-flex items-center gap-1.5 flex-shrink-0 text-xs font-semibold text-gray-500 hover:text-genie-primary transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            All genies
          </button>

          <div className="w-px h-[22px] bg-gray-200 flex-shrink-0" />

          {/* tiny genie identity */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {brandLogoUrl ? (
              <img
                src={brandLogoUrl}
                alt=""
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  objectFit: "contain",
                  backgroundColor: "#f3f4f6",
                  display: "block",
                }}
              />
            ) : (
              <VoiceMascot mascot={mascot} size={26} rounded discSize={30} />
            )}
            <span className="text-sm font-bold text-[#222B45] tracking-[-0.2px] truncate max-w-[160px]">
              {name}
            </span>
            {isLive && (
              <span className="w-[7px] h-[7px] rounded-full bg-genie-success ring-[3px] ring-genie-success/20" />
            )}
          </div>

          <div className="w-px h-[22px] bg-gray-200 flex-shrink-0 hidden md:block" />

          {/* tab nav — the only navigation */}
          <div className="hidden md:flex items-center gap-0.5 p-[3px] rounded-full bg-gray-100 border border-gray-200 flex-shrink-0">
            {TABS.map((tab) => {
              const on = tab.id === activeTab;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setTab(tab.id)}
                  className={`group h-[30px] px-2.5 rounded-full flex items-center gap-1.5 transition-all ${
                    on
                      ? "bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm shadow-genie-primary/30"
                      : "bg-transparent hover:bg-white/60"
                  }`}
                >
                  <Icon className={`w-3.5 h-3.5 ${on ? "text-white" : "text-gray-400 group-hover:text-genie-primary"}`} />
                  <span
                    className={`text-[12.5px] ${
                      on ? "font-bold text-white" : "font-semibold text-gray-500 group-hover:text-genie-primary"
                    }`}
                  >
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* quiet door to the configurator */}
          <button
            type="button"
            onClick={() => navigate(`/genies/${id}/immersive-setup`)}
            className="h-9 px-3.5 rounded-full flex items-center gap-1.5 text-[13px] font-semibold text-gray-500 bg-gray-100 border border-gray-200 hover:text-genie-primary hover:border-genie-primary/40 transition-all"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            Manage
          </button>

          {/* the single change door */}
          <button
            type="button"
            className="h-9 px-4 rounded-full flex items-center gap-1.5 text-[13px] font-bold text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/30 hover:opacity-90 transition-all"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Tell our genie
          </button>
        </div>
      </div>

      {/* mobile tab nav */}
      <div className="md:hidden flex items-center gap-0.5 p-[3px] mx-4 mt-3 rounded-full bg-gray-100 border border-gray-200 overflow-x-auto">
        {TABS.map((tab) => {
          const on = tab.id === activeTab;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setTab(tab.id)}
              className={`group h-[30px] px-3 rounded-full flex items-center gap-1.5 flex-shrink-0 transition-all ${
                on
                  ? "bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm shadow-genie-primary/30"
                  : "bg-transparent hover:bg-white/60"
              }`}
            >
              <Icon className={`w-3.5 h-3.5 ${on ? "text-white" : "text-gray-400 group-hover:text-genie-primary"}`} />
              <span
                className={`text-[12.5px] ${
                  on ? "font-bold text-white" : "font-semibold text-gray-500 group-hover:text-genie-primary"
                }`}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* tab body */}
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="flex flex-col items-center px-6 pt-7 pb-11">
          <div className="w-full max-w-[960px]">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default GenieOwnerDetail;
